import { DynamicField, DynamicSections, Facility, PaymentDetailType } from '@cargos/sprintpay-models';
import moment from 'moment';
import { Detail } from '../details';
import { UserPayments } from '../payment/payment';
import { UserProperties } from './user';

export class EmotransUser implements UserProperties, UserPayments {
    constructor(
        private apis: Record<string, string>,
        public dynamicFields: DynamicField[] = [],
        public dynamicSections: DynamicSections[] = []
    ) {
        const paymentURL = this.apis['payments'];

        this.dynamicSections = [
            {
                id: 1,
                label: 'Emotrans Customer Reference',
                orderNumber: 0,
                company: { id: 57, name: 'Emotrans' },
                emailDomain: null,
            },
            {
                id: 2,
                label: 'Payment details',
                orderNumber: 1,
                company: { id: 57, name: 'Emotrans' },
                emailDomain: null,
            },
            {
                id: 3,
                label: 'Customer Reference',
                orderNumber: 2,
                company: { id: 57, name: 'Emotrans' },
                emailDomain: null,
            },
        ];
        this.dynamicFields = [
            {
                name: 'customerRef',
                label: 'EMO CargoWise shipment/job ID#',
                section: 2,
                fieldType: 'input',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage:
                    'Your own personal reference # so you can remember this payment. For example your file #.',
                validations: [
                    {
                        id: 0,
                        name: 'pattern',
                        value: [
                            /^\d{9,10}$/,
                            /^\d{4}(-\d{6})$/,
                            /^[a-zA-z]{1}\d{4}[a-zA-z]{1}\d{8}$/,
                            /^[a-zA-z]{1}\d{12}$/,
                            /^[a-zA-z]{2}\d{8}$/,
                        ],
                        message:
                            'Enter either as 9 or 10 numeric, or 4 numeric then a “-” followed by 6 numeric, or 1 alpha + 4 numeric + 1 alpha + 8 numeric, or 1 alpha + 12 numeric, or 2 alpha + 8 numeric so as an example 123456789, or 1234567890, or 1234-567890, or S1234L12345678, or E123456789001, or EM12345678.',
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 1,
                        name: 'maxlength',
                        value: '255',
                        message: 'Maximum length exceeded (255 characters maximum)',
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 2,
                        name: 'required',
                        value: 'true',
                        message: 'Please enter your EMO CargoWise shipment/job ID#.',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
            {
                name: 'externalReferenceNum',
                label: 'Customer to be Billed',
                section: 2,
                fieldType: 'input',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage: '',
                validations: [
                    {
                        id: 0,
                        name: 'maxlength',
                        value: '30',
                        message: 'Limit of 30 characters',
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 1,
                        name: 'required',
                        value: 'true',
                        message: 'Required field',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
            {
                name: 'locationId',
                label: 'Location',
                section: 0,
                fieldType: 'dropdown',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage: '',
                apiProviderData: `${paymentURL}/customer/approval-flow/information`,
                validations: [
                    {
                        id: 1,
                        name: 'required',
                        value: 'true',
                        message: 'Required field',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
        ];
    }

    public getPaymentDetails = (payment: PaymentDetailType, facility: Facility, id: string): Detail[] => [
        {
            label: facility?.externalLookup ? 'File Number' : 'Second facility reference #',
            value: payment.hawb ? payment.hawb : 'N/A',
            show: !facility.externalLookup && !id,
        },
        { label: 'Paid To', value: payment.paidTo ? payment.paidTo : 'N/A', show: true },
        {
            label: 'Cargo facility',
            value: facility.name ? facility.name : 'N/A',
            show: true,
        },
        {
            label: 'Amount',
            value: payment.amount ? '$' + payment.amount.toFixed(2) : 'N/A',
            show: true,
        },
    ];

    public getPaymentCustomerReference = (
        payment: PaymentDetailType,
        customerReferenceLabel: string = ''
    ): Detail[] => [
        {
            label: customerReferenceLabel ? customerReferenceLabel : this.dynamicFields[0].label,
            value: payment.customerReference ? payment.customerReference : 'N/A',
            show: true,
        },
        {
            label: 'Customer to be Billed',
            value: payment.externalReferenceNum ? payment.externalReferenceNum : 'N/A',
            show: true,
        },
        {
            label: 'Requester Email Address',
            value: payment.notificationEmail ? payment.notificationEmail : 'N/A',
            show: true,
        },
        { label: 'Notes', value: payment.notes ? payment.notes : 'N/A', show: true },
    ];

    public getCustomerInformation = (payment: PaymentDetailType): Detail[] => [
        {
            label: 'Customer Name',
            value: payment.customer ? payment.customer.name : 'N/A',
            show: true,
        },
        {
            label: 'Notification email',
            value: payment?.notificationEmail ? payment?.notificationEmail : 'N/A',
            show: true,
        },
        {
            label: 'Created Date',
            value: payment?.createdDate ? moment(payment?.createdDate).format('MMM DD, YYYY') : 'N/A',
            show: true,
        },
    ];

    public getCompanyCustomerReference = (payment: PaymentDetailType): Detail[] => [
        {
            label: 'Payment Amount',
            value: payment?.externalData?.type ?? 'N/A',
            show: !!(payment?.externalLookup && payment.id),
        },
        {
            label: 'Type',
            value: payment?.paymentType ?? 'N/A',
            show: !!(payment?.externalLookup && payment.id),
        },
        { label: 'Location', value: payment?.location ?? 'N/A', show: true },
    ];
}
